import { Library } from "lucide-react";
import { PageHeading, Section, Callout, StatusBadge, STATUS_FLOW, Diagram, MockTable } from "../shared";

const STATUS_DETAIL: Record<string, { who: string; from: string[]; to: string[]; rules: string }> = {
  REGISTERED: {
    who: "Set automatically at registration.",
    from: ["—"],
    to: ["IN_PROGRESS", "CANCELLED", "CLOSED"],
    rules: "Default starting status. The owner has been assigned but no work has begun.",
  },
  IN_PROGRESS: {
    who: "An admin moves the item here from Registered.",
    from: ["REGISTERED", "WITH_SDF_MD_REVIEW (return)", "WITH_EDGE_MD_REVIEW (return)", "AWAITING_RESPONSE (return)"],
    to: ["WITH_SDF_MD_REVIEW", "WITH_EDGE_MD_REVIEW", "WITH_SECRETARIAT_TO_SEND", "AWAITING_DISPATCH (above-MD)", "CANCELLED", "CLOSED (with sign-off)"],
    rules: "Active work. The owner adds progress notes; sub-tasks may run in parallel.",
  },
  WITH_SDF_MD_REVIEW: {
    who: "Admin sends from In Progress, or another reviewer hands off.",
    from: ["IN_PROGRESS", "WITH_EDGE_MD_REVIEW (handoff)"],
    to: ["AWAITING_DISPATCH (above-MD lanes)", "WITH_SECRETARIAT_TO_SEND", "IN_PROGRESS (return)", "CLOSED"],
    rules: "Items in this status appear on the MD Meetings → Review Queue. Subject to Transition Permissions if configured.",
  },
  WITH_EDGE_MD_REVIEW: {
    who: "Admin sends from In Progress or from SDF MD Review.",
    from: ["IN_PROGRESS", "WITH_SDF_MD_REVIEW"],
    to: ["WITH_SDF_MD_REVIEW", "WITH_SECRETARIAT_TO_SEND", "IN_PROGRESS (return)"],
    rules: "Used when the lane requires EDGE MD review on top of SDF MD review.",
  },
  WITH_SECRETARIAT_TO_SEND: {
    who: "Admin moves here once review is complete.",
    from: ["IN_PROGRESS (if review not required)", "WITH_SDF_MD_REVIEW", "WITH_EDGE_MD_REVIEW"],
    to: ["SENT_UPWARD (with dispatch note)", "IN_PROGRESS (return)"],
    rules: "Secretariat is preparing the upward submission. Used for non-above-MD-lane items, or by config.",
  },
  AWAITING_DISPATCH: {
    who: "Set automatically when SDF MD approves an above-MD-lane item.",
    from: ["WITH_SDF_MD_REVIEW (auto for above-MD lanes)"],
    to: ["SENT_UPWARD (via Mark Dispatched)"],
    rules: "v2.5.0 status. Held-time clock runs. PMO / Board Sec is notified. Mark Dispatched moves it onward.",
  },
  SENT_UPWARD: {
    who: "Admin (PMO) marks the item dispatched.",
    from: ["AWAITING_DISPATCH (Mark Dispatched)", "WITH_SECRETARIAT_TO_SEND (with dispatch note)"],
    to: ["CLOSED (auto for above-MD lanes; otherwise admin closes after response)"],
    rules: "Dispatch note required. Records recipient, date, channel, and lag. Terminal for above-MD lane items (auto-closes).",
  },
  AWAITING_RESPONSE: {
    who: "Admin sets after sending an item that needs a response.",
    from: ["IN_PROGRESS", "WITH_SECRETARIAT_TO_SEND"],
    to: ["SIGNED_OFF", "CLOSED", "IN_PROGRESS (return)"],
    rules: "Used when waiting on a response from the recipient authority before closure.",
  },
  SIGNED_OFF: {
    who: "Sign-off authority approves the item.",
    from: ["AWAITING_RESPONSE", "Any review stage with sign-off"],
    to: ["CLOSED"],
    rules: "Required gate before close when the item has Sign-off Required.",
  },
  CLOSED: {
    who: "Admin closes with a closing note.",
    from: ["Any non-terminal status (sign-off permitting)"],
    to: ["—"],
    rules: "Terminal. Closing note mandatory. Audit trail records closure event.",
  },
  CANCELLED: {
    who: "Admin cancels the item.",
    from: ["Any non-terminal status"],
    to: ["—"],
    rules: "Terminal. Use when the item is no longer needed at all (different from Return).",
  },
};

export default function StatusReferenceSection() {
  return (
    <div data-testid="guide-status-reference">
      <PageHeading
        icon={Library}
        title="Status Reference Table"
        description="Every action item has a 'status' — a label showing where it is in its journey, from just-logged all the way to finished. This page lists all 11 statuses and explains, for each one, who can set it, where it can come from, and where it can go next."
      />

      <Callout variant="info" title="In plain words">
        A status is just a stage. As work moves along, an admin nudges the item from one stage to the next (for example from <StatusBadge status="REGISTERED" /> to <StatusBadge status="IN_PROGRESS" />). Some stages are "terminal", meaning the item is finished and won't move again.
      </Callout>

      <Section title="How to read this page">
        <p className="text-sm text-white/65 leading-relaxed">
          Each card below shows one status. The <span className="text-white">Set by</span> line says who can put an item into that stage, <span className="text-white">Can come from</span> lists the stages it can arrive from, and <span className="text-white">Can move to</span> lists where it can go next.
        </p>
        <Diagram
          frameLabel="Reading a status card"
          legend={[
            <>The coloured tag is the status name.</>,
            <><span className="text-white">Set by</span> — who is allowed to put the item in this stage.</>,
            <><span className="text-white">Can come from</span> / <span className="text-white">Can move to</span> — the stages before and after.</>,
          ]}
        >
          <div className="flex items-center gap-2">
            <StatusBadge status="IN_PROGRESS" />
            <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">1</span>
          </div>
          <MockTable
            columns={[
              <span className="inline-flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>Set by</span>,
              <span className="inline-flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">3</span>Comes from → moves to</span>,
            ]}
            rows={[
              ["An admin", "Registered → review stages, Closed"],
            ]}
          />
        </Diagram>
      </Section>

      <Section>
        <div className="space-y-3">
          {STATUS_FLOW.map((s) => {
            const detail = STATUS_DETAIL[s.key];
            return (
              <div key={s.key} className="border border-white/5 rounded-md p-3 bg-white/[0.02]" data-testid={`status-ref-${s.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status={s.key} />
                  <span className="text-xs text-white/40 font-mono">{s.key}</span>
                </div>
                <p className="text-xs text-white/55 leading-relaxed mb-2">{s.description}</p>
                {detail && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm mt-2">
                    <KV label="Set by" value={detail.who} />
                    <KV label="Rules" value={detail.rules} />
                    <KV label="Can come from" value={detail.from.join(", ")} />
                    <KV label="Can move to" value={detail.to.join(", ")} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-white/35 uppercase tracking-wider text-2xs">{label}: </span>
      <span className="text-white/65">{value}</span>
    </div>
  );
}
